<template>
  <b-modal
    id="crisis-units-list-modal"
    v-model="localIsActive"
    size="xl"
    hide-footer
    no-close-on-esc
    no-close-on-backdrop
    @hide="onHide"
  >
    <template #modal-title>
      <h4 class="mb-0 text-white">Configuración de instalaciones</h4>
    </template>

    <div v-if="assigning" class="crisis-assignment-overlay">
      <div class="crisis-assignment-panel text-center">
        <b-spinner variant="primary" class="mb-1" />
        <h4 class="mb-50">Asignando instalaciones</h4>
        <p class="mb-1 text-muted">{{ batchMessage }}</p>
        <b-progress
          v-if="batchProgress !== null"
          :value="batchProgress"
          max="100"
          height="8px"
          variant="primary"
        />
      </div>
    </div>

    <FiltersUnits
      :loading="loading || assigning"
      :region-options="regionOptions"
      :selected-states.sync="selectedStates"
      :municipality-options="municipalityOptions"
      :selected-city.sync="selectedCity"
      :search-query.sync="searchQuery"
      @add-installations="addInstallations"
    />

    <div v-if="allSelected" class="alert alert-info py-1 px-2 mt-2 mb-0">
      {{ selectedCountLabel }}
    </div>

    <b-table
      ref="refListTable"
      class="position-relative mt-2"
      :items="fetchList"
      responsive
      :fields="tableColumns"
      primary-key="id"
      :sort-by.sync="sortBy"
      show-empty
      :empty-text="$t('no_record_found')"
      :sort-desc.sync="isSortDirDesc"
      selectable
      select-mode="multi"
      @row-selected="onRowSelected"
    >
      <template #head(selected)>
        <b-form-checkbox
          :checked="allSelected"
          :indeterminate="indeterminate"
          :disabled="assigning"
          @change="toggleSelectAll"
        />
      </template>

      <template #cell(selected)="data">
        <b-form-checkbox
          :checked="isRowSelected(data.item)"
          :disabled="assigning"
          @change="toggleRowSelection(data.item, data.index, $event)"
        />
      </template>

      <template #head()="data">
        <span>{{ data.label }}</span>
      </template>
    </b-table>
    <div class="mx-2 mb-2">
      <b-row>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-start"
        >
          <span class="text-muted text-capitalize">{{
            $t("table.showing", {
              from: dataMeta.from,
              to: dataMeta.to,
              of: dataMeta.of,
            })
          }}</span>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          class="d-flex align-items-center justify-content-center justify-content-sm-end"
        >
          <Pagination
            data-cy="pagination"
            :totalList="totalList"
            :currentPage="currentPage"
            @updatePagination="update"
            :perPage="perPage"
          />
        </b-col>
      </b-row>
    </div>
  </b-modal>
</template>

<script>
import axios from '@/libs/axios';
import store from '@/store';
import { useToast } from "vue-toastification/composition";
import { computed, onMounted, ref, watch } from '@vue/composition-api';
import FiltersUnits from './FiltersUnits.vue';
import Pagination from "@/components/Pagination.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useCrisisAssignmentBatch } from './composables/useCrisisAssignmentBatch';
import { useFullscreenModalTransport } from './composables/useFullscreenModalTransport';

export default {
  props: {
    operationalSecurityCrisis: {
      type: Object
    },
    showModalUnitsList:{
      type: Boolean
    }
  },
  components: {
    FiltersUnits,
    Pagination,
    ToastificationContent
  },
  setup(props, { emit }) {
    onMounted(() => {
      store.dispatch('region/getAll');
      store.dispatch('municipality/getAll');
    });

    const toast = useToast();

    const currentPage = ref(1);
    const isSortDirDesc = ref(true);
    const loading = ref(false);
    const perPage = ref(7);
    const refListTable = ref(null);
    const searchQuery = ref('');
    const selectedCity = ref([]);
    const selectedRows = ref([]);
    const selectedStates = ref([]);
    const sortBy = ref('id');
    const totalList = ref(0);
    const allSelected = ref(false);
    const excludedIds = ref([]);
    const suppressTableRefresh = ref(false);
    const maxUnitsForCloseRefresh = 50;

    const tableColumns = [
      { key: "selected", label: "", thStyle: { width: "50px" } },
      { key: "sirh", label: "SIRH / ID Cajero", sortable: true },
      { key: "description", label: "Sucursal", sortable: true },
      { key: "category.name", label: "Categoría", sortable: true },
      { key: "municipality.name", label: "Municipio", sortable: true },
      { key: "municipality.region.name", label: "Estado", sortable: true },
    ];

    const notify = (title, variant, icon) => {
      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      });
    };

    const crisisId = computed(() => props.operationalSecurityCrisis.id);

    const {
      assigning,
      batchMessage,
      batchProgress,
      resetBatch,
      submitAssignment,
    } = useCrisisAssignmentBatch({
      axios,
      crisisId,
      notify,
      onCompleted: (data) => {
        suppressTableRefresh.value = (data?.units?.length ?? 0) > maxUnitsForCloseRefresh;
        notify('Instalaciones asignadas correctamente', 'success', 'CheckIcon');
        emit('build-data-from-response', data);
        onHide();
      },
    });

    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;

      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalList.value,
      };
    });

    const indeterminate = computed(() => (
      allSelected.value ? excludedIds.value.length > 0 : selectedRows.value.length > 0
    ));

    const localIsActive = computed({
      get() {
        return props.showModalUnitsList;
      },
      set(value) {
        emit("update:isActive", value);
      },
    });

    useFullscreenModalTransport('crisis-units-list-modal', localIsActive);

    const municipalityOptions = computed(() => store.getters['municipality/getSelectedItems']);
    const regionOptions = computed(() => store.getters['region/getSelectedItems']);

    const selectedCountLabel = computed(() => {
      const selected = Math.max(totalList.value - excludedIds.value.length, 0);
      return `${selected} instalaciones serán asignadas con los filtros actuales.`;
    });

    const clearSelection = () => {
      allSelected.value = false;
      excludedIds.value = [];
      selectedRows.value = [];

      if (refListTable.value) {
        refListTable.value.clearSelected();
      }
    };

    const buildFilters = () => ({
      q: searchQuery.value,
      region: selectedStates.value,
      municipality: selectedCity.value,
      tenant: [props.operationalSecurityCrisis.id_tenant],
    });

    const buildAssignmentPayload = () => {
      if (allSelected.value) {
        return {
          selection_mode: 'all_matching_filters',
          filters: buildFilters(),
          include_ids: [],
          exclude_ids: excludedIds.value,
        };
      }

      return {
        selection_mode: 'explicit_ids',
        filters: buildFilters(),
        include_ids: selectedRows.value.map((row) => row.id),
      };
    };

    const addInstallations = async () => {
      if (!allSelected.value && selectedRows.value.length === 0) {
        return;
      }

      await submitAssignment(buildAssignmentPayload());
    };

    const fetchList = (ctx, callback) => {
      if (suppressTableRefresh.value) {
        callback([]);
        return;
      }

      loading.value = true;

      const payload = {
        q: searchQuery.value,
        max: perPage.value,
        page: {
          n: currentPage.value
        },
        region: selectedStates.value,
        municipality: selectedCity.value,
        tenant: [props.operationalSecurityCrisis.id_tenant],
        with: ['municipality:id,name,id_region','usersRh']
      };

      axios.post(`/v1/operational-security-crisis/${props.operationalSecurityCrisis.id}/units/candidates`, payload)
        .then(response => {
          const data = response.data;
          const items = Array.isArray(data?.data)
            ? data.data
            : (Array.isArray(data?.data?.data) ? data.data.data : []);
          const meta = data?.meta ?? data?.data?.meta;

          callback(items);
          totalList.value = meta?.total ?? items.length;
        })
        .catch(() => {
          notify('Error obteniendo la información', 'danger', 'AlertTriangleIcon');
          callback([]);
          totalList.value = 0;
        })
        .finally(() => {
          loading.value = false;
        });
    };

    const isRowSelected = (item) => {
      if (allSelected.value) {
        return !excludedIds.value.includes(item.id);
      }

      return selectedRows.value.some((row) => row.id === item.id);
    };

    const onHide = (event = null) => {
      if (assigning.value && event?.preventDefault) {
        event.preventDefault();
        return;
      }

      currentPage.value = 1;
      searchQuery.value = '';
      selectedCity.value = [];
      selectedStates.value = [];
      clearSelection();
      resetBatch();

      emit('update:isActive', false);

      if (suppressTableRefresh.value) {
        setTimeout(() => {
          suppressTableRefresh.value = false;
        }, 0);
      }
    };

    const onRowSelected = (items) => {
      if (!allSelected.value) {
        selectedRows.value = items;
      }
    };

    const refetchData = () => {
      if (suppressTableRefresh.value) {
        return;
      }

      if (refListTable.value) {
        refListTable.value.refresh();
      }
    };

    const toggleRowSelection = (item, index, checked) => {
      if (allSelected.value) {
        excludedIds.value = checked
          ? excludedIds.value.filter((id) => id !== item.id)
          : [...new Set([...excludedIds.value, item.id])];
        return;
      }

      if (checked) {
        refListTable.value.selectRow(index);
      } else {
        refListTable.value.unselectRow(index);
      }
    };

    const toggleSelectAll = (checked) => {
      allSelected.value = checked;
      excludedIds.value = [];
      selectedRows.value = [];

      if (!refListTable.value) {
        return;
      }

      if (checked) {
        refListTable.value.selectAllRows();
      } else {
        refListTable.value.clearSelected();
      }
    };

    const update = (data) => {
      currentPage.value = data;
    };

    watch([currentPage, perPage, sortBy, isSortDirDesc], () => {
      refetchData();
    });

    watch([selectedStates, searchQuery, selectedCity], () => {
      clearSelection();
      currentPage.value = 1;
      refetchData();
    });

    return {
      assigning,
      batchMessage,
      batchProgress,
      currentPage,
      isSortDirDesc,
      loading,
      perPage,
      refListTable,
      searchQuery,
      selectedCity,
      selectedStates,
      sortBy,
      tableColumns,
      totalList,

      allSelected,
      dataMeta,
      indeterminate,
      localIsActive,
      municipalityOptions,
      regionOptions,
      selectedCountLabel,

      addInstallations,
      fetchList,
      isRowSelected,
      onHide,
      onRowSelected,
      toggleRowSelection,
      toggleSelectAll,
      update
    };
  }
};
</script>

<style scoped>
.crisis-assignment-overlay {
  align-items: center;
  background: rgba(255, 255, 255, 0.88);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 2050;
}

.crisis-assignment-panel {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  max-width: 420px;
  padding: 2rem;
  width: calc(100% - 2rem);
}
</style>
